import React, { useEffect, useState } from 'react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { Dialog } from '@/components/ui/Modal';
import { PageContainer } from '@/components/ui/PageContainer';
import { proposalService, type DestinationContent } from '@/services/ProposalService';
import { useNotificationStore } from '@/stores/notificationStore';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';
import { DestinationEditorModal } from '@/routes/tools/research-proposal/components/WizardSteps/Step4Proposal/DestinationEditorModal';
import {
  Plus,
  MapPin,
  Image as ImageIcon,
  Pencil,
  Trash2,
  AlertTriangle,
} from 'lucide-react';

export function DestinationLibrary() {
  const notifySuccess = useNotificationStore(s => s.success);
  const notifyError = useNotificationStore(s => s.error);
  const { hasAnyPermission } = usePermissions();

  const [loading, setLoading] = useState(false);
  const [destinations, setDestinations] = useState<DestinationContent[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal state
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState<DestinationContent | null>(null);
  const [newDestinationCity, setNewDestinationCity] = useState('');
  const [newDestinationCountry, setNewDestinationCountry] = useState('');

  // Create new destination modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createCity, setCreateCity] = useState('');
  const [createCountry, setCreateCountry] = useState('');

  // Delete confirmation — holds the destination being deleted plus the
  // cascade preview so the user knows what activities/events go with it.
  const [deleteTarget, setDeleteTarget] = useState<{
    destination: DestinationContent;
    preview: { activities: number; events: number } | null;
    loading: boolean;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Permission checks
  const canCreate = hasAnyPermission([PERMISSIONS.MANAGE_DESTINATIONS, PERMISSIONS.MANAGE_ORGANIZATION_SETTINGS]);
  const canUpdate = hasAnyPermission([PERMISSIONS.MANAGE_DESTINATIONS, PERMISSIONS.MANAGE_ORGANIZATION_SETTINGS]);
  const canDelete = hasAnyPermission([PERMISSIONS.MANAGE_DESTINATIONS, PERMISSIONS.MANAGE_ORGANIZATION_SETTINGS]);

  async function loadDestinations() {
    setLoading(true);
    try {
      const items = await proposalService.listDestinationContent();
      setDestinations(items);
    } catch (e: any) {
      notifyError(e?.message || 'Failed to load destinations');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadDestinations(); }, []);

  function openCreate() {
    setCreateCity('');
    setCreateCountry('');
    setCreateModalOpen(true);
  }

  function handleCreateSubmit() {
    if (!createCity.trim() || !createCountry.trim()) {
      notifyError('Please enter both city and country');
      return;
    }
    // Check if destination already exists
    const exists = destinations.find(
      d => d.city.toLowerCase() === createCity.trim().toLowerCase() &&
           d.country.toLowerCase() === createCountry.trim().toLowerCase()
    );
    if (exists) {
      notifyError('This destination already exists');
      return;
    }
    setNewDestinationCity(createCity.trim());
    setNewDestinationCountry(createCountry.trim());
    setSelectedDestination(null);
    setCreateModalOpen(false);
    setEditorOpen(true);
  }

  function openEdit(destination: DestinationContent) {
    setSelectedDestination(destination);
    setNewDestinationCity(destination.city);
    setNewDestinationCountry(destination.country);
    setEditorOpen(true);
  }

  function closeEditor() {
    setEditorOpen(false);
    setSelectedDestination(null);
    setNewDestinationCity('');
    setNewDestinationCountry('');
  }

  async function handleEditorSave() {
    await loadDestinations();
    notifySuccess('Destination saved successfully');
  }

  async function openDeleteConfirm(destination: DestinationContent) {
    // Open the dialog immediately with a "loading" state, then fetch the
    // cascade preview so the user can see how many activities/events will
    // also be removed.
    setDeleteTarget({ destination, preview: null, loading: true });
    try {
      const preview = await proposalService.previewDestinationContentDelete(destination.id);
      setDeleteTarget(prev =>
        prev && prev.destination.id === destination.id
          ? { destination, preview: { activities: preview.activities, events: preview.events }, loading: false }
          : prev
      );
    } catch (e: any) {
      notifyError(e?.message || 'Failed to load delete preview');
      setDeleteTarget(null);
    }
  }

  async function handleConfirmDelete() {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      const result = await proposalService.deleteDestinationContent(deleteTarget.destination.id);
      const parts = [`Deleted "${deleteTarget.destination.city}"`];
      if (result.deletedActivities > 0) {
        parts.push(`${result.deletedActivities} activit${result.deletedActivities === 1 ? 'y' : 'ies'}`);
      }
      if (result.deletedEvents > 0) {
        parts.push(`${result.deletedEvents} event${result.deletedEvents === 1 ? '' : 's'}`);
      }
      notifySuccess(parts.join(' · '));
      setDeleteTarget(null);
      await loadDestinations();
    } catch (e: any) {
      notifyError(e?.message || 'Failed to delete destination');
    } finally {
      setIsDeleting(false);
    }
  }

  // Filter destinations by search term
  const filteredDestinations = destinations.filter(dest =>
    searchTerm === '' ||
    dest.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dest.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (dest.region && dest.region.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Sort by city name
  const sortedDestinations = [...filteredDestinations].sort((a, b) =>
    a.city.localeCompare(b.city)
  );

  // Get content completeness stats
  function getCompleteness(dest: DestinationContent) {
    let score = 0;
    let total = 4; // Now only 4 categories since weather/pricing moved to proposal-specific
    if (dest.short_description) score++;
    if (dest.images && dest.images.length > 0) score++;
    if (dest.key_pros && dest.key_pros.length > 0) score++;
    if (dest.considerations && dest.considerations.length > 0) score++;
    return { score, total };
  }

  return (
    <PageContainer>
      <PageHeader
        title="Destination Library"
        description="Manage destination content that can be reused across proposals"
      >
        {canCreate && (
          <Button size="sm" onClick={openCreate} leftIcon={<Plus className="w-4 h-4" />}>
            New Destination
          </Button>
        )}
      </PageHeader>

      {/* Search Bar */}
      <div className="mb-6">
        <Input
          placeholder="Search by city, country, or region..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          leftIcon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          }
        />
      </div>

      {/* Stats */}
      <div className="mb-6 text-sm text-gray-500">
        {sortedDestinations.length} destination{sortedDestinations.length !== 1 ? 's' : ''}
        {searchTerm && ` matching "${searchTerm}"`}
      </div>

      {/* Loading State */}
      {loading && destinations.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : sortedDestinations.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <MapPin className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'No destinations found' : 'No destinations yet'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm
                ? 'Try adjusting your search'
                : 'Get started by adding your first destination to the library'}
            </p>
            {!searchTerm && canCreate && (
              <Button onClick={openCreate} leftIcon={<Plus className="w-4 h-4" />}>
                New Destination
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedDestinations.map((destination) => {
            const completeness = getCompleteness(destination);
            const hasImages = destination.images && destination.images.length > 0;
            const previewImage = hasImages ? destination.images[0].url : null;

            return (
              <Card
                key={destination.id}
                className="group relative overflow-hidden cursor-pointer hover:shadow-lg transition-shadow duration-200"
                onClick={() => canUpdate && openEdit(destination)}
              >
                {/* Preview Image */}
                {previewImage ? (
                  <div className="h-32 bg-gray-100 overflow-hidden">
                    <img
                      src={previewImage}
                      alt={`${destination.city}, ${destination.country}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="h-32 bg-gray-100 flex items-center justify-center">
                    <ImageIcon className="w-12 h-12 text-gray-300" />
                  </div>
                )}

                <CardContent className="pt-4 pb-4">
                  {/* Location */}
                  <div className="flex items-start gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-medium text-gray-900 leading-tight">
                        {destination.city}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {destination.country}
                        {destination.region && ` · ${destination.region}`}
                      </p>
                    </div>
                  </div>

                  {/* Short Description */}
                  {destination.short_description && (
                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                      {destination.short_description}
                    </p>
                  )}

                  {/* Content Stats */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {hasImages && (
                      <Badge variant="secondary" className="text-xs">
                        <ImageIcon className="w-3 h-3 mr-1" />
                        {destination.images.length} image{destination.images.length !== 1 ? 's' : ''}
                      </Badge>
                    )}
                    {destination.key_pros && destination.key_pros.length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {destination.key_pros.length} pros
                      </Badge>
                    )}
                    {destination.considerations && destination.considerations.length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {destination.considerations.length} considerations
                      </Badge>
                    )}
                  </div>

                  {/* Completeness indicator */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary-500 rounded-full transition-all"
                        style={{ width: `${(completeness.score / completeness.total) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500">
                      {completeness.score}/{completeness.total}
                    </span>
                  </div>

                  {/* Action Buttons */}
                  {(canUpdate || canDelete) && (
                    <div className="flex items-center justify-end gap-1 mt-3 pt-3 border-t border-gray-100">
                      {canUpdate && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEdit(destination);
                          }}
                          className="text-gray-600 hover:text-primary-600 hover:bg-primary-50"
                          aria-label="Edit destination"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDeleteConfirm(destination);
                          }}
                          className="text-gray-600 hover:text-error-600 hover:bg-error-50"
                          aria-label="Delete destination"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create New Destination Modal */}
      <Dialog
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title="New Destination"
        description="Enter the city and country to create a new destination"
      >
        <form onSubmit={(e) => { e.preventDefault(); handleCreateSubmit(); }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">City *</label>
            <Input
              value={createCity}
              onChange={(e) => setCreateCity(e.target.value)}
              placeholder="e.g., Paris"
              required
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Country *</label>
            <Input
              value={createCountry}
              onChange={(e) => setCreateCountry(e.target.value)}
              placeholder="e.g., France"
              required
            />
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="ghost" onClick={() => setCreateModalOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!createCity.trim() || !createCountry.trim()}
            >
              Continue
            </Button>
          </div>
        </form>
      </Dialog>

      {/* Destination Editor Modal */}
      {editorOpen && (newDestinationCity || selectedDestination) && (
        <DestinationEditorModal
          isOpen={editorOpen}
          onClose={closeEditor}
          city={newDestinationCity || selectedDestination?.city || ''}
          country={newDestinationCountry || selectedDestination?.country || ''}
          existingContent={selectedDestination}
          onSave={handleEditorSave}
          onActivitiesChanged={loadDestinations}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        isOpen={!!deleteTarget}
        onClose={() => !isDeleting && setDeleteTarget(null)}
        title="Delete destination"
        description={
          deleteTarget
            ? `Remove "${deleteTarget.destination.city}, ${deleteTarget.destination.country}" from the library?`
            : ''
        }
      >
        {deleteTarget && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 rounded-md bg-warning-50 border border-warning-200">
              <AlertTriangle className="w-5 h-5 text-warning-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-warning-900 space-y-1">
                <p className="font-medium">This will permanently delete:</p>
                {deleteTarget.loading ? (
                  <p className="text-warning-800">Loading impact…</p>
                ) : deleteTarget.preview ? (
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>The destination card &amp; description</li>
                    {deleteTarget.preview.activities > 0 && (
                      <li>
                        {deleteTarget.preview.activities} activit
                        {deleteTarget.preview.activities === 1 ? 'y' : 'ies'} for this exact city/country
                      </li>
                    )}
                    {deleteTarget.preview.events > 0 && (
                      <li>
                        {deleteTarget.preview.events} event
                        {deleteTarget.preview.events === 1 ? '' : 's'} for this exact city/country
                      </li>
                    )}
                    {deleteTarget.preview.activities === 0 && deleteTarget.preview.events === 0 && (
                      <li>(no related activities or events)</li>
                    )}
                  </ul>
                ) : null}
              </div>
            </div>
            <p className="text-sm text-gray-600">
              Existing proposals that already use this destination are{' '}
              <span className="font-medium">not affected</span> — they keep their own copy of the
              activities, events, and content.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDeleteTarget(null)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleConfirmDelete}
                disabled={isDeleting || deleteTarget.loading}
                isLoading={isDeleting}
                leftIcon={<Trash2 className="w-4 h-4" />}
              >
                Delete destination
              </Button>
            </div>
          </div>
        )}
      </Dialog>
    </PageContainer>
  );
}
